import { storage as storageInstance } from './utility/storage';
import { OPTED_IN_STORAGE_KEY } from './constants';

export interface Consent {
  isImplicitlyOptedOut(args: { optOutByDefault: boolean }): boolean;
  markAsExplicitlyOptedOut(a: { intervalDaysToStorageExpiration: number; cookieDomain?: string }): void;
  updateExpiration(args: { intervalDaysToStorageExpiration: number; cookieDomain?: string }): void;
  isOptedIn(): boolean;
  optIn(args: { intervalDaysToStorageExpiration: number; cookieDomain?: string }): boolean;
  optOut(args: { intervalDaysToStorageExpiration: number; cookieDomain?: string }): boolean;
}

type Storage = typeof storageInstance;
type P<K extends keyof Consent> = Parameters<Consent[K]>[0];
type R<K extends keyof Consent> = ReturnType<Consent[K]>;

const OPTED_OUT_VALUE = '0';
const OPTED_IN_VALUE = '1';

export class UserConsent implements Consent {
  public static create(storage: Storage) {
    return new UserConsent(storage);
  }

  /* eslint no-useless-constructor:0 */
  /* eslint no-empty-function:0 */
  private constructor(private storage: Storage) {}
  /* eslint no-empty-function:1 */
  /* eslint no-useless-constructor:1 */

  public isImplicitlyOptedOut({ optOutByDefault }: P<'isImplicitlyOptedOut'>): R<'isImplicitlyOptedOut'> {
    return optOutByDefault && this.getOptedInStateFromStorage() === null;
  }

  public markAsExplicitlyOptedOut({
    intervalDaysToStorageExpiration,
    cookieDomain,
  }: P<'markAsExplicitlyOptedOut'>): R<'markAsExplicitlyOptedOut'> {
    this.storage.removeReproEntries();
    this.storage.set(OPTED_IN_STORAGE_KEY, OPTED_OUT_VALUE, {
      expires: intervalDaysToStorageExpiration,
      domain: cookieDomain,
    });
  }

  public updateExpiration({
    intervalDaysToStorageExpiration,
    cookieDomain,
  }: P<'updateExpiration'>): R<'updateExpiration'> {
    this.storage.set(
      OPTED_IN_STORAGE_KEY,
      this.getOptedInStateFromStorage() === OPTED_OUT_VALUE ? OPTED_OUT_VALUE : OPTED_IN_VALUE,
      {
        expires: intervalDaysToStorageExpiration,
        domain: cookieDomain,
      }
    );
  }

  public isOptedIn(): R<'isOptedIn'> {
    // Checking 0 instead of 1 to allowed tracking when cookie value is null (i.e. when the cookie does not exist)
    return this.getOptedInStateFromStorage() !== OPTED_OUT_VALUE;
  }

  public optIn({ intervalDaysToStorageExpiration, cookieDomain }: P<'optIn'>): R<'optIn'> {
    if (this.isOptedIn()) {
      console.warn('Already opted in');
      return false;
    }

    this.storage.set(OPTED_IN_STORAGE_KEY, OPTED_IN_VALUE, {
      expires: intervalDaysToStorageExpiration,
      domain: cookieDomain,
    });
    return true;
  }

  public optOut({ intervalDaysToStorageExpiration, cookieDomain }: P<'optOut'>): R<'optOut'> {
    if (!this.isOptedIn()) {
      console.warn('Already opted out');
      return false;
    }

    this.storage.removeReproEntries();
    this.storage.set(OPTED_IN_STORAGE_KEY, OPTED_OUT_VALUE, {
      expires: intervalDaysToStorageExpiration,
      domain: cookieDomain,
    });

    return true;
  }

  private getOptedInStateFromStorage() {
    return this.storage.get(OPTED_IN_STORAGE_KEY);
  }
}
